import styled from "@emotion/styled"
import Typography from "@mui/material/Typography"
import { richTextListStyles } from "../../common/richTextListStyles"
import { colors } from "../../theme/colors"
import { breakpoints } from "../../theme/mediaQueries"
import { sizes } from "../../theme/sizes"

export const Root = styled.div`
  width: 100%;
  background-color: ${colors.primitives.neutral0};
  padding: ${sizes.spacing[48]}px 0;

  @media (min-width: ${breakpoints.lg}px) {
    padding: ${sizes.spacing[64]}px 0 ${sizes.spacing[112]}px;
  }
`

export const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${sizes.spacing[8]}px;
  margin-bottom: ${sizes.spacing[40]}px;

  @media (min-width: ${breakpoints.lg}px) {
    margin-bottom: ${sizes.spacing[80]}px;
  }
`

export const SectionTitle = styled(Typography)`
  font-size: 32px;
  font-weight: 700;
  line-height: 38px;
  color: ${colors.light.text.primary};

  @media (min-width: ${breakpoints.lg}px) {
    font-size: 48px;
    line-height: 56px;
  }
`

export const ChevronWrapper = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;

  svg {
    width: 16px;
    height: 27px;

    @media (min-width: ${breakpoints.lg}px) {
      width: 19px;
      height: 32px;
    }
  }
`

export const ContentWrapper = styled.div`
  position: relative;
`

export const DesktopPathWrapper = styled.div`
  display: none;

  @media (min-width: ${breakpoints.lg}px) {
    display: block;
    position: absolute;
    top: 50%;
    inset-inline: 0;
    transform: translateY(-50%);
    z-index: 0;
    pointer-events: none;
  }

  svg {
    width: 100%;
    height: auto;
    display: block;
  }
`

export const MobilePathWrapper = styled.div`
  position: absolute;
  inset: 0;
  z-index: 0;
  pointer-events: none;

  @media (min-width: ${breakpoints.lg}px) {
    display: none;
  }

  svg {
    width: 100%;
    height: 100%;
    display: block;
  }
`

export const CardsRow = styled.div<{ $count: number }>`
  display: flex;
  flex-direction: column;
  gap: ${sizes.spacing[8]}px;
  position: relative;
  z-index: 1;

  @media (min-width: ${breakpoints.lg}px) {
    /* EGDE-121/124: spread the cards into N equal columns across the FULL row
       width, each card centred in its column. The decorative dashed weave
       (DesktopPathWrapper) splits the same width into N even columns and places
       its lane transitions on the column boundaries — so even, full-width
       distribution is what keeps the weave in the gaps between cards instead of
       slicing through them. The previous justify-content:center + flex-basis
       clustered the cards in the middle, which is why the weave was misaligned.
       gap is 0 here on purpose: each column is wider than the 220px card, so the
       inter-card spacing comes from the column, matching the path's DW/count. */
    display: grid;
    grid-template-columns: repeat(${(p) => p.$count}, 1fr);
    justify-items: center;
    /* EGDE-668: START, not center. Centring each card in the row meant a card
       with one line less CMS copy than its neighbours floated down by half the
       difference, so its title and body no longer sat on the same lines as the
       rest — QA: "the title and description appear a row below the others".
       The row height is still the tallest card, so the centred dashed weave
       (DesktopPathWrapper) and the tallest card do not move; only the shorter
       cards rise into line. */
    align-items: start;
    gap: 0;
  }
`

export const Card = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  max-width: 220px;
  min-height: 200px;
  justify-content: center;

  &:nth-child(odd) {
    align-self: flex-end;
  }

  &:nth-child(even) {
    align-self: flex-start;
  }

  @media (min-width: ${breakpoints.lg}px) {
    /* EGDE-606: never wider than the grid column. The dashed connector puts its
       lane transitions on the column boundaries (DW/count), so a card that
       overflows its 1fr column runs straight over the track — visible from six
       cards up, where the column drops below 220px. */
    max-width: min(220px, 100%);
    min-height: 148px;
    /* EGDE-668: the 148px floor would otherwise re-introduce the same drop for
       any card whose copy is shorter than it — justify-content:center would
       centre that content inside the floor. Start the content at the top so
       the grid's align-items:start actually reaches the text. */
    justify-content: flex-start;

    &:nth-child(odd),
    &:nth-child(even) {
      align-self: auto;
    }
  }
`

export const CardTitle = styled(Typography)`
  font-size: 24px !important;
  font-weight: 700 !important;
  line-height: 28px !important;
  letter-spacing: 0.15%;
  color: ${colors.light.text.primary};
  margin-bottom: ${sizes.spacing[8]}px;
`

// EGDE-121: cap the card body at CARD_BODY_MAX_LINES with an ellipsis on EVERY
// viewport. On the desktop row an over-long entry overflowed past the centred
// connector and broke the row; on mobile it made one card far taller than the
// rest (and the design shows uniform ~5-line cards). Clamping keeps the cards a
// consistent height in both layouts — normal Figma copy fits well under the cap,
// only verbose CMS entries get truncated.
const CARD_BODY_MAX_LINES = 7
export const CardBody = styled.div`
  font-size: 18px;
  font-weight: 500;
  line-height: 22px;
  color: ${colors.light.text.primary};
  word-wrap: break-word;
  overflow-wrap: break-word;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: ${CARD_BODY_MAX_LINES};
  line-clamp: ${CARD_BODY_MAX_LINES};
  overflow: hidden;

  @media (min-width: ${breakpoints.lg}px) {
    font-size: 20px;
    line-height: 28px;
    letter-spacing: 0.15%;
  }

  p {
    margin: 0;
  }

  /* EGDE-607: rich-text hyperlinks render blue and underlined, as on every other
     rich-text surface. !important overrides the inline color:#008761 that
     replaceRichTextLinks writes onto every anchor. */
  & a {
    color: ${colors.primitives.linkBlue} !important;
    text-decoration: underline;
  }

  /* EGDE-604/605: the global reset strips list padding, so the default outside
     markers had nothing to sit in and both list types read as plain text. The
     shared styles draw the brand chevron / decimal markers inside — identical to
     the Rich Content Block and person cards (cf. EGDE-330, EGDE-546). */
  ${richTextListStyles}

  /* The card copy is centre-aligned, which would strand an inline-start marker
     far from its text across the full card width. Centre the list as a block and
     let the rows read from the inline start, so marker and text stay together. */
  & ul,
  & ol {
    width: fit-content;
    max-width: 100%;
    margin-inline: auto;
    text-align: start;
  }
`
